// Generic auto-editable text: makes any block-level text element editable
// without hand-wrapping each string. Slots are a stable hash of the element's
// pathname + tag + ORIGINAL text (stored in data-ag-orig), so edits persist
// across reloads and apply for all visitors — robust to transient siblings.
import { txt } from "@/data/siteText";

const INLINE = new Set([
  "SPAN", "A", "B", "STRONG", "EM", "I", "U", "SUP", "SUB", "BR",
  "SMALL", "MARK", "ABBR", "TIME", "CODE", "S", "DEL",
]);
const BLOCK_TEXT = new Set(["P", "H1", "H2", "H3", "H4", "H5", "H6", "LI", "BLOCKQUOTE", "FIGCAPTION", "DT", "DD"]);
const SELECTOR = "p,h1,h2,h3,h4,h5,h6,li,blockquote,figcaption,dt,dd";

export function autoTextEnabled(pathname) {
  return pathname.startsWith("/product");
}

function hashSlot(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  return "auto_" + h.toString(36);
}

function isEligible(el) {
  if (!BLOCK_TEXT.has(el.tagName)) return false;
  const text = (el.innerText || "").trim();
  if (!text || text.length > 1200) return false;
  for (const c of el.children) if (!INLINE.has(c.tagName)) return false; // only inline children
  if (el.closest("nav,header,footer,[data-no-autotext],[data-sonner-toaster],[role=dialog]")) return false;
  return true;
}

// Original text is captured on first sight and frozen in data-ag-orig so the
// slot stays stable even after the visible text is overridden.
function origText(el) {
  let o = el.getAttribute("data-ag-orig");
  if (o == null) {
    o = (el.innerText || "").trim();
    el.setAttribute("data-ag-orig", o);
  }
  return o;
}

function slotFor(pathname, el, seen) {
  const base = `${pathname}|${el.tagName}|${origText(el)}`;
  const n = seen.get(base) || 0;
  seen.set(base, n + 1);
  return hashSlot(n ? `${base}|${n}` : base);
}

// Iterate outermost eligible text blocks, invoking cb(el, slot).
function eachAutoText(pathname, cb) {
  const seen = new Map();
  document.querySelectorAll(SELECTOR).forEach((el) => {
    if (el.parentElement && el.parentElement.closest("[data-text-slot]")) return;
    if (el.hasAttribute("data-text-slot") && !el.hasAttribute("data-auto-text")) return;
    if (el.querySelector("[data-text-slot]:not([data-auto-text])")) return; // holds a manual slot
    if (!isEligible(el)) return;
    cb(el, slotFor(pathname, el, seen));
  });
}

// Tag eligible elements with a stable auto slot (used in edit mode).
export function assignAutoSlots(pathname) {
  if (!autoTextEnabled(pathname)) return;
  eachAutoText(pathname, (el, slot) => {
    if (el.getAttribute("data-text-slot") !== slot) {
      el.setAttribute("data-text-slot", slot);
      el.setAttribute("data-auto-text", "1");
    }
  });
}

// Apply saved overrides to the live DOM (runs for every visitor).
export function applyAutoText(pathname) {
  if (!autoTextEnabled(pathname)) return;
  eachAutoText(pathname, (el, slot) => {
    const val = txt(slot, null);
    if (val != null && el.innerText !== val) el.innerText = val;
  });
}
